import {CommonModule} from '@angular/common';
import {Component, Inject} from '@angular/core';
import {ReportFilterParamSummary} from '@gatewee/types/report';
import {TranslateModule} from '@ngx-translate/core';
import {GateweeIconComponent} from '../gatewee-icon/gatewee-icon.component';
import {TuiButton, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';

export interface GateweeReportExportDialogData {
  title: string;
  hint: string;
  filterParams: ReportFilterParamSummary[];
  confirmLabel?: string;
  cancelLabel?: string;
}

@Component({
  selector: 'gatewee-report-export-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule, TuiButton, GateweeIconComponent],
  template: `
    <div class="gw-p-6 gw-space-y-4">
      <h2 class="gw-text-lg gw-font-semibold gw-text-text-primary">
        {{ data.title }}
      </h2>

      <div
        class="gw-flex gw-items-start gw-gap-2x-small gw-rounded gw-border gw-border-border-normal gw-bg-background-neutral-1 gw-p-small"
      >
        <gatewee-icon
          icon="@phi.info"
          size="1.25rem"
          iconStyle="primary"
          weight="bold"
          class="gw-shrink-0 gw-mt-0.5"
        ></gatewee-icon>
        <p class="gw-m-0 gw-min-w-0 gw-text-sm gw-text-text-secondary">
          {{ data.hint }}
        </p>
      </div>

      <div class="gw-space-y-2x-small">
        <span class="gw-text-sm gw-font-medium gw-text-text-primary">
          {{ 'features.reportExport.parametersTitle' | translate }}
        </span>

        <ng-container *ngIf="data.filterParams.length; else noFilters">
          <ul class="gw-space-y-2x-small gw-list-none gw-p-0 gw-m-0">
            <li
              *ngFor="let param of data.filterParams; trackBy: trackByParam"
              class="gw-flex gw-flex-wrap gw-items-baseline gw-gap-2x-small gw-rounded gw-border gw-border-border-abnormal gw-bg-background-neutral-2 gw-px-small gw-py-2x-small"
            >
              <span class="gw-text-xs gw-font-medium gw-text-text-secondary"
                >{{ param.label }}:</span
              >
              <span class="gw-text-sm gw-text-text-primary">{{
                param.value
              }}</span>
            </li>
          </ul>
        </ng-container>

        <ng-template #noFilters>
          <p class="gw-text-sm gw-text-text-secondary">
            {{ 'features.reportExport.noFilters' | translate }}
          </p>
        </ng-template>
      </div>

      <div class="gw-flex gw-justify-end gw-gap-2 gw-pt-2">
        <button
          tuiButton
          appearance="secondary"
          type="button"
          (click)="cancel()"
        >
          {{ data.cancelLabel ?? ('common.actions.cancel' | translate) }}
        </button>
        <button
          tuiButton
          appearance="primary"
          type="button"
          (click)="confirm()"
        >
          {{ data.confirmLabel ?? ('common.actions.ok' | translate) }}
        </button>
      </div>
    </div>
  `,
})
export class GateweeReportExportDialogComponent {
  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<
      boolean,
      GateweeReportExportDialogData
    >,
  ) {}

  get data(): GateweeReportExportDialogData {
    return this.context.data;
  }

  trackByParam(_: number, param: ReportFilterParamSummary): string {
    return `${param.label}:${param.value}`;
  }

  confirm(): void {
    this.context.completeWith(true);
  }

  cancel(): void {
    this.context.completeWith(false);
  }
}
